import { Position } from 'context/GeoLocationContext';
import { logPaidRequest, log } from './config';
import { googleMapsLoader } from './googleMapsLoader';

interface LocationPromiseResolver {
  resolve: (value: LocationResult) => void;
  reject: (error: any) => void;
}

interface LocationResult {
  position: Position;
  address?: string;
  source: 'google_estimation' | 'browser_geolocation' | 'ip_geolocation';
}

type AddressUpdateCallback = (position: Position, address: string) => void;

class LocationManager {
  private static instance: LocationManager;
  private estimationPromise: Promise<LocationResult> | null = null;
  private estimationSubscribers: LocationPromiseResolver[] = [];
  private isEstimating = false;
  private addressUpdateCallbacks: Map<string, AddressUpdateCallback> = new Map();

  private constructor() {}

  static getInstance(): LocationManager {
    if (!LocationManager.instance) {
      LocationManager.instance = new LocationManager();
    }
    return LocationManager.instance;
  }

  /**
   * Get estimated location - ensures only one API call is made even if called multiple times
   */
  async getEstimatedLocation(
    sessionId: string,
    geocodingCallback: (latitude: number, longitude: number) => Promise<string | null>,
    addressUpdateCallback?: AddressUpdateCallback
  ): Promise<LocationResult> {
    console.log('🔒 [LocationManager] getEstimatedLocation called', {
      isEstimating: this.isEstimating,
      hasPromise: !!this.estimationPromise,
      subscribers: this.estimationSubscribers.length,
      timestamp: new Date().toISOString()
    });

    // If we're already estimating, return the existing promise
    if (this.estimationPromise) {
      console.log('🔒 [LocationManager] Returning existing estimation promise');
      return this.estimationPromise;
    }

    // If we're in the process of creating the promise, queue this request
    if (this.isEstimating) {
      console.log('🔒 [LocationManager] Already estimating, queuing request');
      return new Promise((resolve, reject) => {
        this.estimationSubscribers.push({ resolve, reject });
      });
    }

    // Mark that we're starting estimation
    this.isEstimating = true;

    // Create the promise
    this.estimationPromise = this.performEstimation(sessionId, geocodingCallback, addressUpdateCallback)
      .finally(() => {
        // Notify all subscribers
        const subscribers = [...this.estimationSubscribers];
        this.estimationSubscribers = [];
        
        if (this.estimationPromise) {
          this.estimationPromise.then(
            result => subscribers.forEach(s => s.resolve(result)),
            error => subscribers.forEach(s => s.reject(error))
          );
        }
        
        this.isEstimating = false;
      });

    return this.estimationPromise;
  }

  private async performEstimation(
    sessionId: string,
    geocodingCallback: (latitude: number, longitude: number) => Promise<string | null>,
    addressUpdateCallback?: AddressUpdateCallback
  ): Promise<LocationResult> {
    console.log('🔒 [LocationManager] Performing ONE estimation request');
    
    try {
      logPaidRequest('Geolocation', {
        sessionId,
        method: 'Google Geolocation API',
        costEstimate: '$0.005 per request',
        apiEndpoint: '/geolocation/v1/geolocate',
        note: 'Singleton request - shared across all hook instances'
      });

      const response = await fetch(
        `https://www.googleapis.com/geolocation/v1/geolocate?key=${process.env.GOOGLE_MAPS_API_KEY}`,
        {
          method: 'POST',
        }
      );
      
      const data = await response.json();
      const latitude = data.location.lat;
      const longitude = data.location.lng;
      const position = {
        latitude: latitude,
        longitude: longitude,
      };

      const result: LocationResult = {
        position,
        address: undefined,
        source: 'google_estimation'
      };

      // Store the callback for this session
      if (addressUpdateCallback) {
        this.addressUpdateCallbacks.set(sessionId, addressUpdateCallback);
      }
      
      // Try to get address immediately if Google Maps is loaded
      if (googleMapsLoader.isGoogleMapsLoaded()) {
        try {
          const address = await geocodingCallback(latitude, longitude);
          if (address) {
            log('🔒 [LocationManager] Got address immediately:', address);
            result.address = address;
          }
        } catch (error) {
          console.error('🔒 [LocationManager] Failed to get address:', error);
        }
      }

      // If we don't have an address yet, try again after Google Maps loads
      if (!result.address) {
        log('🔒 [LocationManager] Scheduling geocoding after Google Maps loads');
        
        // Don't block on this - return position immediately
        googleMapsLoader.ensureLoaded().then(async () => {
          try {
            const address = await geocodingCallback(latitude, longitude);
            if (address) {
              log('🔒 [LocationManager] Got address after Google Maps loaded:', address);
              
              // Update all registered callbacks
              this.addressUpdateCallbacks.forEach(callback => {
                callback(result.position, address);
              });
            }
          } catch (error) {
            console.error('🔒 [LocationManager] Failed to get address after retry:', error);
          }
        }).catch(error => {
          console.error('🔒 [LocationManager] Google Maps failed to load:', error);
        });
      }

      return result;
    } catch (error) {
      console.error('🔒 [LocationManager] Estimation failed:', error);
      throw error;
    }
  }

  /**
   * Clear the cached promise to allow a new estimation
   */
  clearCache() {
    this.estimationPromise = null;
    this.estimationSubscribers = [];
    this.isEstimating = false;
    this.addressUpdateCallbacks.clear();
  }
  
  /**
   * Register a callback for address updates
   */
  registerAddressUpdateCallback(sessionId: string, callback: AddressUpdateCallback) {
    this.addressUpdateCallbacks.set(sessionId, callback);
  }
  
  /**
   * Unregister a callback
   */
  unregisterAddressUpdateCallback(sessionId: string) {
    this.addressUpdateCallbacks.delete(sessionId);
  }
}

export const locationManager = LocationManager.getInstance();